<template>
  <UIDialog
    :is-open
    :z-index
    title="Connect"
    size="3md"
    @close="handleClose"
  >
    <template #title>
      <div class="add-account__title">
        <UIButtonBack
          v-if="isMoreOptions"
          @click="isMoreOptions = false"
        />
        <span>{{ addAccountTitle }}</span>
      </div>
    </template>
    <div
      v-if="isMoreOptions"
      class="add-account__buttons"
    >
      <UIButton
        v-for="button in buttonsList"
        :key="button.label"
        :label="button.label"
        :disabled="loading"
        full
        variant="secondary"
        :data-refid="button.dataRefid"
        @click="button.action()"
      />
    </div>
    <template v-else>
      <template v-if="hasExistingConnectors">
        <div class="add-account__caption">
          Use existing connector:
        </div>
        <div class="add-account__list">
          <ExistingConnector
            v-for="connector in existingConnectors"
            :key="connector.id"
            :connector
            @click="handleClickExisting(connector)"
          />
        </div>
        <div class="add-account__caption">
          or new:
        </div>
      </template>
      <InstitutionsSearch
        ref="institutionsSearchRef"
        :loading
        @click-institution="handleClickInstitution"
      />
    </template>
    <template
      v-if="!isMoreOptions"
      #footer
    >
      <UIButton
        label="More options"
        full
        variant="secondary"
        data-refid="connectorsMoreOptions"
        @click="isMoreOptions = true"
      />
    </template>
  </UIDialog>
</template>

<script setup lang="ts">
import { ComputedRef, computed, inject, markRaw, ref, useTemplateRef, watch } from 'vue'

import { LinkedDataConnector, ModalClass } from '@types'
import { LinkedDataConnectors } from '../../utils/enums'
import { InstatutionData } from '../../utils/types'

import { POWER_MODE } from '@/const'

import { connectAccount } from './utils/helpers'
import { handleCatchedError } from '@/helpers/common'

import { useNotifications } from '@/plugins/notification'
import { useLinkedDataConnector } from '@/hooks/linkedDataConnector'

import { useLinkedDataConnectorsStore } from '@/store/linkedData/connectors'
import { useLinkedDataStore } from '@/store/linkedData'
import { useModalsStore } from '@/store/modals'

import { UIDialog } from '@ui/modals'
import { UIButton, UIButtonBack } from '@ui/buttons'
import ExistingConnector from './ExistingConnector.vue'
import InstitutionsSearch from './InstitutionsSearch.vue'
import AddAccountAkoya from './AddAccountAkoya.vue'
import AddAccountOFX from './AddAccountOFX.vue'
import AddAccountInstructions from './AddAccountInstructions.vue'

import { CONNECTOR_INSTRUCTIONS } from '../../utils/instructions'

type Props = {
  modal: ModalClass<any>
}

const props = defineProps<Props>()

const linkedDataStore = useLinkedDataStore()
const linkedDataConnectorsStore = useLinkedDataConnectorsStore()
const modalsStore = useModalsStore()

const { error } = useNotifications()

const institutionsSearchRef = useTemplateRef('institutionsSearchRef')

const { plaid, yodlee } = useLinkedDataConnector((flag: boolean) => {
  flag ? props.modal.show(modalsStore.getZIndex()) : props.modal.close()
})

const handlePlaid = (institutionId?: string, routingNumber?: string | null) => {
  loading.value = true
  plaid(institutionId, routingNumber).finally(() => loading.value = false)
}

const handleYodlee = (institutionId?: string) => {
  loading.value = true
  yodlee(institutionId).finally(() => loading.value = false)
}

const loading = ref(false)

const isMoreOptions = ref(false)

const existingConnectors = ref<LinkedDataConnector[]>()

const isPowerMode = inject<ComputedRef<boolean>>(POWER_MODE)

const zIndex = computed(() => props.modal.zIndex)
const isOpen = computed(() => props.modal.isOpened)

const addAccountTitle = computed(() =>
  isMoreOptions.value ? 'More options' : 'Connect',
)

const hasExistingConnectors = computed(() => existingConnectors.value?.length)

const buttonsList = computed(() => {
  const list = [
    ...(isPowerMode?.value ? [
      {
        label: `Use ${LinkedDataConnectors.PLAID} connector`,
        action: handlePlaid,
      },
      {
        label: `Use ${LinkedDataConnectors.YODLEE} connector`,
        action: handleYodlee,
      },
    ] : []),
    {
      label: `Use ${LinkedDataConnectors.OFX} connector`,
      dataRefid: 'useOfxConnector',
      action: () => {
        props.modal.close()
        const modalInstance = modalsStore.init(
          'add-account-ofx-dialog',
          markRaw(AddAccountOFX),
        )
        modalInstance?.open(modalsStore.getZIndex(), {
          onCancel: handleCancel,
        })
      },
    },
  ]
  return list
})

const handleClose = () => {
  props.modal.close()
}

const handleCancel = () => {
  props.modal.show(modalsStore.getZIndex())
}

const handleClickExisting = async ({ id, name }: LinkedDataConnector) => {
  props.modal.close()
  loading.value = true
  try {
    await linkedDataConnectorsStore.map(id)
    await Promise.all([
      linkedDataStore.fetch(),
      linkedDataConnectorsStore.fetch(),
    ])
  } catch (e) {
    handleCatchedError(e as string)
    error({
      message: `Error while mapping Existing connector "${name}"`,
    })
    return
  } finally {
    loading.value = false
  }
}

const proceedWithInstitution = async (item: InstatutionData) => {
  switch (item.source) {
    case 'plaid':
      handlePlaid(item.institution_id, item.routing_number)
      break
    case 'yodlee':
      loading.value = true
      await handleYodlee(item.institution_id)
      loading.value = false
      break
    case 'akoya':
      loading.value = true
      connectAccount(item.source, item).then(({ name, url, logo }) => {
        const modalInstance = modalsStore.init(
          'add-account-akoya-dialog',
          markRaw(AddAccountAkoya),
        )
        modalInstance?.open(modalsStore.getZIndex(), {
          name,
          url,
          logo,
          onCancel: handleCancel,
        })
      }).finally(() => {
        loading.value = false
      })
      break
    default:
      loading.value = true
      connectAccount(item.source, item).then(({ url }) => {
        location.href = url
      }).finally(() => {
        loading.value = false
      })
  }
}

const handleClickInstitution = (item: InstatutionData) => {
  const instructions = CONNECTOR_INSTRUCTIONS[item.institution_id]
  if (!instructions) {
    proceedWithInstitution(item)
    return
  }

  props.modal.close()
  const modalInstance = modalsStore.init(
    'add-account-instructions-dialog',
    markRaw(AddAccountInstructions),
  )
  modalInstance?.open(modalsStore.getZIndex(), {
    instructions,
    onCancel: handleCancel,
    onContinue: () => proceedWithInstitution(item),
  })
}

watch(
  isOpen,
  async value => {
    if (!value) {
      institutionsSearchRef.value?.clearSearch()
      return
    }
    try {
      const data = await linkedDataConnectorsStore.fetchExistingConnectors()
      existingConnectors.value = data
    } catch (e) {
      handleCatchedError(e as string)
      return
    }
  },
  { immediate: true },
)
</script>

<style scoped>
.add-account {
  &__title {
    @apply flex items-center;
    @apply gap-2;
  }

  &__caption {
    @apply mb-2 py-1;
    @apply rounded;
    @apply font-medium text-sm sm:text-xs;
    @apply text-center;
    @apply bg-indigo-50 dark:bg-indigo-900;
    @apply text-indigo-600 dark:text-indigo-400;
  }

  &__list {
    @apply mb-4;
    @apply grid gap-2 sm:grid-cols-2;
  }

  &__buttons {
    @apply space-y-4;
  }
}
</style>
